import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Home } from './pages/Home';
import { Cart } from './pages/Cart';
import { Checkout } from './pages/Checkout';
import { Profile } from './pages/Profile';
import { CartItem, Product } from './types';
import { mockProducts } from './data/mockData';
type Page = 'home' | 'cart' | 'checkout' | 'profile';
export function App() {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [cart, setCart] = useState<CartItem[]>([]);
  const STIPEND_TOTAL = 200;
  const TAX_RATE = 0.085;
  // Auto-recalculate coverage whenever cart changes
  useEffect(() => {
    setCart(prevCart => {
      // Get all plan-approved items
      const planApprovedItems = prevCart.filter(item => item.product.rdRecommendation === 'recommended').sort((a, b) => a.product.price - b.product.price); // Sort by price (cheapest first)
      const nonApprovedItems = prevCart.filter(item => item.product.rdRecommendation !== 'recommended');
      // Calculate which items fit in stipend (including their tax)
      let runningTotal = 0;
      const newCart: CartItem[] = [];
      for (const item of planApprovedItems) {
        const itemSubtotal = item.product.price * item.quantity;
        const itemTax = itemSubtotal * TAX_RATE;
        const itemTotal = itemSubtotal + itemTax;
        if (runningTotal + itemTotal <= STIPEND_TOTAL) {
          // Fits in stipend
          newCart.push({
            ...item,
            isCovered: true
          });
          runningTotal += itemTotal;
        } else {
          // Doesn't fit in stipend
          newCart.push({
            ...item,
            isCovered: false
          });
        }
      }
      // All non-approved items are not covered
      for (const item of nonApprovedItems) {
        newCart.push({
          ...item,
          isCovered: false
        });
      }
      return newCart;
    });
  }, [cart.length, ...cart.map(item => `${item.product.id}-${item.quantity}`)]);
  const handleAddToCart = (product: Product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      const isPlanApproved = product.rdRecommendation === 'recommended';
      if (existingItem) {
        toast.success(`Added another ${product.name} to cart`, {
          description: isPlanApproved ? 'Plan-approved item' : 'Extra item',
          duration: 2000
        });
        return prevCart.map(item => item.product.id === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      toast.success(`${product.name} added to cart!`, {
        description: isPlanApproved ? 'Plan-approved item' : 'Extra item',
        duration: 2000
      });
      // Start with isCovered = false, useEffect will recalculate
      return [...prevCart, {
        product,
        quantity: 1,
        isCovered: false
      }];
    });
  };
  const handleSwapProduct = (currentProduct: Product, alternativeId: string) => {
    const alternative = mockProducts.find(p => p.id === alternativeId);
    if (alternative) {
      toast.success(`Swapped to ${alternative.name}!`, {
        description: 'Great healthy choice 🎉',
        duration: 2000
      });
      handleAddToCart(alternative);
    }
  };
  const handleSwapItem = (currentProductId: string, alternativeId: string) => {
    const alternative = mockProducts.find(p => p.id === alternativeId);
    if (!alternative) return;
    setCart(prevCart => {
      const itemToSwap = prevCart.find(item => item.product.id === currentProductId);
      if (!itemToSwap) return prevCart;
      toast.success(`Swapped to ${alternative.name}!`, {
        description: 'Great healthy choice 🎉',
        duration: 2000
      });
      const withoutOld = prevCart.filter(item => item.product.id !== currentProductId);
      const existingAlternative = withoutOld.find(item => item.product.id === alternativeId);
      if (existingAlternative) {
        return withoutOld.map(item => item.product.id === alternativeId ? {
          ...item,
          quantity: item.quantity + itemToSwap.quantity
        } : item);
      }
      return [...withoutOld, {
        product: alternative,
        quantity: itemToSwap.quantity,
        isCovered: false // Will be recalculated by useEffect
      }];
    });
  };
  const handleUpdateQuantity = (productId: string, quantity: number) => {
    setCart(prevCart => prevCart.map(item => item.product.id === productId ? {
      ...item,
      quantity
    } : item));
  };
  const handleRemoveItem = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };
  const handlePlaceOrder = () => {
    setCart([]);
    setCurrentPage('home');
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-0">
      <Toaster position="top-center" richColors data-id="element-1" />

      {currentPage === 'home' && <Home cart={cart} onAddToCart={handleAddToCart} onSwapProduct={handleSwapProduct} onNavigateToCart={() => setCurrentPage('cart')} onNavigateToProfile={() => setCurrentPage('profile')} data-id="element-2" />}

      {currentPage === 'cart' && <Cart cart={cart} onUpdateQuantity={handleUpdateQuantity} onRemoveItem={handleRemoveItem} onSwapItem={handleSwapItem} onNavigateBack={() => setCurrentPage('home')} onNavigateToCheckout={() => setCurrentPage('checkout')} onNavigateToProfile={() => setCurrentPage('profile')} data-id="element-3" />}

      {currentPage === 'checkout' && <Checkout cart={cart} onNavigateBack={() => setCurrentPage('cart')} onNavigateToProfile={() => setCurrentPage('profile')} onPlaceOrder={handlePlaceOrder} data-id="element-4" />}

      {currentPage === 'profile' && <Profile cart={cart} onNavigateBack={() => setCurrentPage('home')} onNavigateToCart={() => setCurrentPage('cart')} data-id="element-5" />}
    </div>;
}